import { TextFieldProps } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { AutocompleteOptions } from 'types/autocomplete'
import _ from 'lodash'
import Select, { SingleValue } from 'react-select'
import styles from './AutocompleteField.module.scss'
import { HookAutocompleteClientOrBrokerDebtorsFormField } from 'hooks/AutocompleteFormField/useAutocompleteClientOrBrokerDebtors'
import { HookAutocompleteFormField } from 'hooks/AutocompleteFormField/useAutocomplete'
import { HookAutocompleteFormFieldTimezone } from 'hooks/AutocompleteFormField/useAutocompleteTimeZone'

type AutocompleteHooks =
  | HookAutocompleteFormField
  | HookAutocompleteFormFieldTimezone
  | HookAutocompleteClientOrBrokerDebtorsFormField

type AutocompleteFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  options?: AutocompleteOptions[]
  label: string
  value?: string | AutocompleteOptions | null
  state: AutocompleteHooks
  debounceTime?: number
  required?: boolean
  readonly?: boolean
  marginTop?: boolean
  selectId?: string
  onChange: (value: SingleValue<AutocompleteOptions>) => void
  errorMessage?: string
  height?: number
  width?: string | number
  borderRadius?: number
}

export function autocompleteValue(val: string | AutocompleteOptions | null | undefined): string {
  val = val ?? ''
  if (typeof val !== 'string') {
    return val.value
  }
  return val
}

const AutocompleteField = (props: AutocompleteFieldProps) => {
  const {
    selectId,
    value,
    required = false,
    disabled,
    options,
    debounceTime = 1000,
    state,
    label,
    readonly = false,
    marginTop = true,
    onChange,
    errorMessage,
    height = 56,
    width = '100%',
    borderRadius = 4,
  } = props

  const handleDebounce = useMemo(() => {
    return _.debounce((value: string) => {
      state.setSearchValue(value)
    }, debounceTime)
  }, [debounceTime, state])

  useEffect(() => {
    return () => {
      handleDebounce.cancel()
    }
  }, [handleDebounce])

  const selectClass = `${styles.autocomplete} ${marginTop && styles.autocompleteMarginTop}`

  const formattedValue = autocompleteValue(value)

  return (
    <div className={styles.autocompleteContainer}>
      <Select
        id={selectId}
        className={selectClass}
        placeholder={`${label} ${required ? '*' : ''}`}
        onChange={(e) => {
          onChange(e)
        }}
        onInputChange={(value, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            handleDebounce(value)
          }
        }}
        value={formattedValue ? options?.find((option) => option.value === formattedValue) : null}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        menuIsOpen={readonly ? false : undefined}
        isClearable={readonly ? false : true}
        isSearchable={readonly ? false : true}
        options={options}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base: any) => ({ ...base, zIndex: 9999, marginTop: '-6px' }),
          control: (base: any) => ({
            ...base,
            height: height,
            width: width,
            boxShadow: 'none',
            borderColor: errorMessage ? '#f44336' : '#9e9e9e',
            '&:hover': {
              borderColor: errorMessage ? '#f44336' : '#4b4b4b',
            },
            borderRadius: borderRadius,
          }),
          placeholder: (base: any) => ({ ...base, color: errorMessage ? '#f44336' : '#9e9e9e' }),
        }}
      />

      {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
    </div>
  )
}
export default AutocompleteField
